import React from 'react';
import { motion } from 'framer-motion';
import styles from './CreationProgressModal.module.scss';
import ModalOverlay from '../ModalOverlay/ModalOverlay.tsx';
import { CheckCircle, AlertCircle, Loader2, Clock, ExternalLink } from 'lucide-react';

type ProgressState = 'creating' | 'uploading' | 'success' | 'pending' | 'error';

interface CreationProgressModalProps {
  isOpen: boolean;
  state: ProgressState;
  message: string;
  progress?: {
    current: number;
    total: number;
  };
  projectId?: string;
  projectPreviewUrl?: string;
  onSuccess?: () => void;
  onError?: () => void;
  onViewPreview?: () => void;
  successButtonLabel?: string;
  errorButtonLabel?: string;
}

const modalVariants = {
  hidden: { opacity: 0, scale: 1.1, y: 20 },
  visible: {
    opacity: 1,
    scale: 1.0,
    y: 0,
    transition: { 
      delay: 0.1,
      duration: 0.3,
      ease: "easeOut"
    },
  },
};

const CreationProgressModal: React.FC<CreationProgressModalProps> = ({
  isOpen,
  state,
  message,
  progress,
  projectId,
  projectPreviewUrl,
  onSuccess,
  onError,
  onViewPreview,
  successButtonLabel = 'Ir a mis documentaciones',
  errorButtonLabel = 'Cerrar'
}) => {
  const isLoading = state === 'creating' || state === 'uploading';
  const isSuccess = state === 'success';
  const isPending = state === 'pending';
  const isError = state === 'error';

  return (
    <ModalOverlay 
      isOpen={isOpen} 
      preventClose={isLoading}
      onClose={isError ? onError : undefined}
    >
      <motion.div
        className={styles.modal}
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <div className={styles.modalBody}>
          <div className={styles.content}>
            {/* Icono según el estado */}
            <div className={styles.iconContainer}>
              {isLoading && (
                <Loader2 size={48} className={`${styles.loadingIcon} ${styles.spinning}`} />
              )}
              {isSuccess && (
                <CheckCircle size={48} className={styles.successIcon} />
              )}
              {isPending && (
                <Clock size={48} className={styles.pendingIcon} />
              )}
              {isError && (
                <AlertCircle size={48} className={styles.errorIcon} />
              )}
            </div>

            {/* Título según el estado */}
            <h3 className={styles.title}>
              {isLoading && 'Procesando...'}
              {isSuccess && '¡Anteproyecto actualizado con éxito!'}
              {isPending && '¡Anteproyecto creado!'}
              {isError && '¡Ups! Algo salió mal'}
            </h3>

            {/* Mensaje */}
            <p className={styles.bodyText}>{message}</p>

            {/* Barra de progreso opcional */}
            {isLoading && progress && (
              <div className={styles.progressContainer}>
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressFill} 
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
                <p className={styles.progressText}>
                  {progress.current} de {progress.total} archivos subidos
                </p>
              </div>
            )}
          </div>

          {/* Botones - Solo si NO está loading */}
          {!isLoading && (
            <div className={styles.options}>
              {isSuccess && onSuccess && (
                <button
                  className={`${styles.button} ${styles.primaryButton}`}
                  onClick={onSuccess}
                >
                  {successButtonLabel}
                </button>
              )}
              {isPending && (
                <>
                  {onViewPreview && projectPreviewUrl && (
                    <button
                      className={`${styles.button} ${styles.primaryButton}`}
                      onClick={onViewPreview}
                    >
                      <ExternalLink size={18} />
                      Ver Vista Previa
                    </button>
                  )}
                  {onSuccess && (
                    <button
                      className={`${styles.button} ${styles.secondaryButton}`}
                      onClick={onSuccess}
                    >
                      Ir a mis documentaciones
                    </button>
                  )}
                </>
              )}
              {isError && onError && (
                <button
                  className={`${styles.button} ${styles.primaryButton}`}
                  onClick={onError}
                >
                  {errorButtonLabel}
                </button>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </ModalOverlay>
  );
};

export default CreationProgressModal;
